import { useEffect, useState } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Phone,
  MessageCircle,
  Mail,
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Building2,
  Star,
  ArrowRight,
  Navigation,
  Linkedin,
  Twitter
} from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';

import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { formatOpeningHoursDayRange, hasOpeningHoursData } from '@/lib/openingHours';
import { openStoreDirections } from '@/lib/storeMapUrls';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';

const Contact = () => {
  const { config } = useAppData();
  const allStores = config.stores || [];
  const mainStore = allStores[0]; // Primeira loja para hero section
  const { colors } = useTheme();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  useEffect(() => {
    document.title = `Contato - ${config.name}`;
  }, [config.name]);

  const formatOpeningHours = (openingHours: any) => {
    const days = [
      { key: 'monday', label: 'Segunda-feira' },
      { key: 'tuesday', label: 'Terça-feira' },
      { key: 'wednesday', label: 'Quarta-feira' },
      { key: 'thursday', label: 'Quinta-feira' },
      { key: 'friday', label: 'Sexta-feira' },
      { key: 'saturday', label: 'Sábado' },
      { key: 'sunday', label: 'Domingo' },
    ];

    return days.map((day) => ({
      day: day.label,
      hours: formatOpeningHoursDayRange(openingHours, day.key),
    }));
  };

  const renderStoreCard = (store: any, showBadges = false) => (
    <div className="bg-card rounded-3xl border border-border/50 overflow-hidden shadow-xl">
      <div className="p-8 md:p-10">
        <div className="max-w-7xl mx-auto">
          {/* Store Header with Badges */}
          {showBadges && (
            <div className="flex items-center gap-2 mb-6">
              <h3 className="text-2xl font-bold">{store.company}</h3>
              {store.virtual && (
                <span className="text-xs bg-blue-500/20 text-blue-600 px-2 py-1 rounded-full font-medium">
                  Virtual
                </span>
              )}
              {store.hidden && (
                <span className="text-xs bg-gray-500/20 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full font-medium">
                  Oculta
                </span>
              )}
            </div>
          )}

          {/* Image + Store Info Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-[480px_1fr] gap-8 mb-10">
            {/* Left: Store Image - Fixed 480x280 */}
            <div className="relative w-full max-w-[480px] mx-auto lg:mx-0">
              <div className="relative w-full aspect-[12/7] overflow-hidden rounded-2xl group shadow-xl">
                <img
                  src={store.frontage}
                  alt={`Fachada ${store.company}`}
                  className="w-full h-full object-cover transition-transform duration-700"
                />
                {/* Gradient Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />

                {/* Store Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white z-10">
                  <h4 className="text-xl font-bold mb-1 drop-shadow-lg line-clamp-1">{store.company}</h4>
                  <div className="flex items-start gap-1.5 text-white/90">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p className="text-xs line-clamp-2">{store?.place?.address || 'Endereço da empresa'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Store Info + Contact Cards */}
            <div className="flex flex-col justify-between">
              <div>
                {!showBadges && (
                  <>
                    <h2 className="text-3xl md:text-4xl font-bold mb-3">{store.company}</h2>
                    <p className="text-lg text-muted-foreground mb-6">
                      Sua próxima experiência automotiva começa aqui.
                    </p>
                  </>
                )}
                {showBadges && store.place?.city && (
                  <p className="text-lg text-muted-foreground mb-6">
                    {store.place.city} - {store.place?.state || ''}
                  </p>
                )}

                {/* Contact Methods Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {/* Phone */}
                  {store.phone?.ios && (
                    <a
                      href={store.phone?.ios}
                      className="group bg-gradient-to-br rounded-2xl p-6 border border-blue-500/20 hover:border-blue-500/40 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-blue-500/20 rounded-xl group-hover:bg-blue-500/30 transition-colors">
                          <Phone className="h-6 w-6 text-blue-500" />
                        </div>
                        <h5 className="font-bold text-lg">Telefone</h5>
                      </div>
                      <p className="text-base text-foreground font-semibold mb-1">{store.phone?.formatted}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-blue-500 transition-colors">
                        <span>Ligar agora</span>
                        <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </div>
                    </a>
                  )}

                  {/* WhatsApp */}
                  {store.whatsapp?.ios && (
                    <button
                      onClick={() => openWhatsApp(store.whatsapp?.ios)}
                      className="group bg-gradient-to-br rounded-2xl p-6 border border-green-500/20 hover:border-green-500/40 hover:shadow-lg transition-all duration-300 text-left w-full"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-green-500/20 rounded-xl group-hover:bg-green-500/30 transition-colors">
                          <MessageCircle className="h-6 w-6 text-green-500" />
                        </div>
                        <h5 className="font-bold text-lg">WhatsApp</h5>
                      </div>
                      <p className="text-base text-foreground font-semibold mb-1">{store.whatsapp?.formatted}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-green-500 transition-colors">
                        <span>Enviar mensagem</span>
                        <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  )}

                  {/* Address */}
                  {store.place && (() => {
                    const distance = getDistanceToStore(userLocation, store);
                    return (
                      <div
                        onClick={async () => {
                          // Request location only when user clicks
                          if (!userLocation) {
                            const location = await requestUserLocation();
                            if (location) {
                              setUserLocation(location);
                            }
                          }
                          const place = store.place;
                          openStoreDirections(place);
                        }}
                        className="group bg-gradient-to-br rounded-2xl p-6 border border-red-500/20 hover:border-red-500/40 hover:shadow-lg transition-all duration-300 cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-red-500/20 rounded-xl group-hover:bg-red-500/30 transition-colors">
                              <MapPin className="h-6 w-6 text-red-500" />
                            </div>
                            <div>
                              <h5 className="font-bold text-lg">Localização</h5>
                              {distance !== null && (
                                <Badge
                                  variant="secondary"
                                  className="inline-flex items-center gap-1 px-2 py-1 text-white font-medium"
                                  style={{ backgroundColor: secondaryColor }}
                                >
                                  <Navigation className="h-3 w-3" />
                                  <span className="text-xs">{formatDistance(distance)}</span>
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-foreground font-medium mb-1 line-clamp-2 leading-relaxed">
                          {store.place?.address || 'Endereço da empresa'}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-red-500 transition-colors">
                          <span>Como chegar</span>
                          <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Opening Hours - Modern Design */}
          {hasOpeningHoursData(store.openingHours) && (
            <div>
              <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-4 rounded-2xl shadow-lg" style={{ background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}15)` }}>
                    <Clock className="h-8 w-8" style={{ color: primaryColor }} />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Horários de Funcionamento</h4>
                    <p className="text-sm text-muted-foreground">Confira nossos horários de atendimento</p>
                  </div>
                </div>

                {/* Schedule Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {formatOpeningHours(store.openingHours).map((schedule, idx) => (
                    <div
                      key={idx}
                      className="group relative bg-gradient-to-br from-background/90 to-background/60 backdrop-blur-sm rounded-2xl p-4 border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                    >
                      {/* Day Badge */}
                      <div className="flex items-center justify-center mb-3">
                        <div className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                          {schedule.day.substring(0, 3).toUpperCase()}
                        </div>
                      </div>

                      {/* Hours */}
                      <div className="text-center">
                        <div className="text-sm font-medium text-muted-foreground mb-1">
                          {schedule.day}
                        </div>
                        <div
                          className={`text-base font-bold ${schedule.hours === 'Fechado' ? 'text-red-500' : ''}`}
                          style={schedule.hours !== 'Fechado' ? { color: primaryColor } : {}}
                        >
                          {schedule.hours}
                        </div>
                      </div>

                      {/* Hover Accent */}
                      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: `0 0 0 2px ${primaryColor}20` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <Header config={config} />

      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <section className="py-20 mt-[70px]">
          {/* About Card - Modern Design */}
          <div className="">
            <div className="relative">
              {/* Decorative Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl" style={{ background: primaryColor }} />
                <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl" style={{ background: secondaryColor }} />
              </div>

              <div className="relative p- md:p-12">
                {/* Header */}
                <div className="flex items-start gap-6 mb-8">
                  <div className="flex-shrink-0">
                    <img
                      src={config.icon?.light}
                      alt={config.name}
                      className="h-20 w-auto object-contain rounded-lg"
                    />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-3xl md:text-4xl font-bold" style={{ color: titleColor }}>
                      Sobre a {config.name}
                    </h2>
                    <p className="text-lg" style={{ color: textColor }}>
                      Nossa história, valores e compromisso com você
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full" style={{ background: `linear-gradient(to bottom, ${primaryColor}, ${secondaryColor})` }} />
                  <div className="pl-8 text-lg leading-relaxed whitespace-pre-line" style={{ color: textColor }}>
                    {config.about}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All Stores Section */}
        {allStores.length >= 1 && (
          <section className="mb-20">
            <div className="space-y-10">
              {allStores.map((store, index) => (
                <div key={store._id || index}>
                  {renderStoreCard(store, false)}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Social Media & Quick Actions */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-3xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Conecte-se Conosco</h2>
            <p className="text-muted-foreground mb-10 max-w-2xl mx-auto">
              Siga nossas redes sociais e fique por dentro das novidades, promoções e lançamentos
            </p>

            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              {/* Social Media Links */}
              <div className="flex flex-wrap items-center justify-center gap-4">
                {config.socialNetworks?.instagram && (
                  <a
                    href={config.socialNetworks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all duration-300"
                  >
                    <Instagram className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                    <span className="font-semibold">Instagram</span>
                  </a>
                )}

                {config.socialNetworks?.facebook && (
                  <a
                    href={config.socialNetworks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-blue-600 text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all duration-300"
                  >
                    <Facebook className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                    <span className="font-semibold">Facebook</span>
                  </a>
                )}

                {config.socialNetworks?.youtube && (
                  <a
                    href={config.socialNetworks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-red-600 text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all duration-300"
                  >
                    <Youtube className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                    <span className="font-semibold">YouTube</span>
                  </a>
                )}

                {config.socialNetworks?.tiktok && (
                  <a
                    href={config.socialNetworks.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-black text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all duration-300"
                  >
                    <svg className="h-5 w-5 group-hover:rotate-12 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                    <span className="font-semibold">TikTok</span>
                  </a>
                )}

                {config.socialNetworks?.linkedin && (
                  <a
                    href={config.socialNetworks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-blue-700 text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all duration-300"
                  >
                    <Linkedin className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                    <span className="font-semibold">LinkedIn</span>
                  </a>
                )}

                {config.socialNetworks?.twitter && (
                  <a
                    href={config.socialNetworks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-3 bg-black text-white px-6 py-3 rounded-2xl hover:scale-105 transition-all duration-300"
                  >
                    <Twitter className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                    <span className="font-semibold">X (Twitter)</span>
                  </a>
                )}
              </div>

              {/* Email Card - Right Side */}
              {config.email && (
                <a
                  href={`mailto:${config.email}`}
                  className="group flex items-center gap-3 border border-border hover:border-primary/40 bg-card px-6 py-3 rounded-2xl hover:scale-105 hover:shadow-lg transition-all duration-300"
                >
                  <Mail className="h-5 w-5 group-hover:rotate-12 transition-transform" style={{ color: primaryColor }} />
                  <span className="font-semibold">{config.email}</span>
                </a>
              )}
            </div>
          </div>
        </section>

        {/* App Download Section */}
        {(hasGooglePlayLink || hasAppStoreLink) && (
        <section>
          <div className="text-background rounded-3xl p-12 text-center relative overflow-hidden mb-20" style={{ background: primaryColor }}>
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/10" />
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4">Baixe nosso App</h2>
              <p className="text-background/80 mb-8 text-lg max-w-2xl mx-auto">
                Tenha acesso completo ao nosso estoque, financiamentos, promoções e muito mais,
                tudo na palma da sua mão
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                {hasGooglePlayLink && (
                  <a
                    href={config.googlePlay.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={googlePlay}
                      alt="Disponível no Google Play"
                      className="h-16 w-auto object-contain"
                    />
                  </a>
                )}

                {hasAppStoreLink && (
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={appStore}
                      alt="Baixar na App Store"
                      className="h-16 w-auto object-contain"
                    />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
        )}
      </div>

      <Footer config={config} />
    </div>
  );
};

export default Contact;
